import { Direction } from '@angular/cdk/bidi';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UtilsService } from '../utils/utils.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import {
  ARABIC,
  DAYS_NAMES,
  ENGLISH,
  HEBREW,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { SelectOption } from '../../interfaces/select-option.interface';

export interface Language {
  label: string;
  language: string;
  symbol: string;
  direction: Direction;
  code: number;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private _languageOptions: {
    hebrew: Language;
    english: Language;
    arabic: Language;
  };
  private _selectedLanguage$: BehaviorSubject<Language> = new BehaviorSubject<Language>(null);
  private readonly LANGUAGE_STORAGE_KEY = 'app.language';

  public daysOfTheWeek$: Observable<SelectOption<Date>[]> = of([
    { label: 'ראשון', value: this.utilService.getNextDateOfDay(0) },
    { label: 'שני', value: this.utilService.getNextDateOfDay(1) },
    { label: 'שלישי', value: this.utilService.getNextDateOfDay(2) },
    { label: 'רביעי', value: this.utilService.getNextDateOfDay(3) },
    { label: 'חמישי', value: this.utilService.getNextDateOfDay(4) },
    { label: 'שישי', value: this.utilService.getNextDateOfDay(5) },
    { label: 'שבת', value: this.utilService.getNextDateOfDay(6) },
  ]);

  constructor(
    private readonly utilService: UtilsService,
    private readonly localStorageService: LocalStorageService
  ) {
    this._languageOptions = {
      hebrew: {
        label: HEBREW.hebrew,
        symbol: 'he',
        language: HEBREW.english,
        direction: 'rtl',
        code: 1,
      },
      english: {
        label: this.utilService.convertFirstLatterCapital(ENGLISH.english),
        symbol: 'en',
        language: ENGLISH.english,
        direction: 'ltr',
        code: 2,
      },
      arabic: {
        label: ARABIC.arabic,
        symbol: 'ar',
        language: ARABIC.english,
        direction: 'rtl',
        code: 3,
      },
    };

    this.selectedLanguage$.subscribe((language) => {
      this.daysOfTheWeek$ = of([
        { label: DAYS_NAMES[language.language][0], value: this.utilService.getNextDateOfDay(0) },
        { label: DAYS_NAMES[language.language][1], value: this.utilService.getNextDateOfDay(1) },
        { label: DAYS_NAMES[language.language][2], value: this.utilService.getNextDateOfDay(2) },
        { label: DAYS_NAMES[language.language][3], value: this.utilService.getNextDateOfDay(3) },
        { label: DAYS_NAMES[language.language][4], value: this.utilService.getNextDateOfDay(4) },
        { label: DAYS_NAMES[language.language][5], value: this.utilService.getNextDateOfDay(5) },
        { label: DAYS_NAMES[language.language][6], value: this.utilService.getNextDateOfDay(6) },
      ])
    })

    // Initialize with saved language or default to Hebrew
    const savedLanguage = this.localStorageService.getItem<string>(
      this.LANGUAGE_STORAGE_KEY
    );
    if (savedLanguage && this._languageOptions[savedLanguage]) {
      this._selectedLanguage$.next(
        this._languageOptions[savedLanguage]
      );
    } else {
      this._selectedLanguage$.next(
        this.languageOptions.hebrew
      );
    }
  }

  get languageOptions(): {
    hebrew: Language;
    english: Language;
    arabic: Language;
  } {
    return this._languageOptions;
  }

  get selectedLanguage$() {
    return this._selectedLanguage$.asObservable();
  }

  getSelectedLanguage(): Language {
    return this._selectedLanguage$.getValue();
  }

  changeLanguage(language: string) {
    const selected = this._languageOptions[language];
    if (selected) {
      this._selectedLanguage$.next(selected);
      // Save language preference to localStorage
      this.localStorageService.setItem(this.LANGUAGE_STORAGE_KEY, language);
    }
  }

  changeLanguageBySymbol(symbol: string) {
    const selected = Object.values(this._languageOptions).find(language => language.symbol === symbol);
    if (selected) {
      this._selectedLanguage$.next(selected);
      // Save language preference to localStorage
      this.localStorageService.setItem(this.LANGUAGE_STORAGE_KEY, selected.symbol);
    }
  }
}
